/**
 * House of Anuk — Theme JavaScript
 * Slow, intentional, ceremonial. Every interaction breathes.
 */

/* ── Page loader ───────────────────────────────────────────── */
(function initLoader() {
  const loader = document.getElementById('page-loader');
  if (!loader) return;
  window.addEventListener('load', () => {
    loader.classList.add('is-hidden');
    setTimeout(() => loader.remove(), 800);
  });
})();

/* ── Sticky header ─────────────────────────────────────────── */
(function initStickyHeader() {
  const header = document.getElementById('site-header');
  if (!header) return;

  let lastY = 0;
  const onScroll = () => {
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > 60);
    header.classList.toggle('is-hidden-up', y > lastY && y > 120);
    lastY = y;
  };

  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* ── Mobile navigation ─────────────────────────────────────── */
(function initMobileNav() {
  const toggle = document.querySelector('[data-menu-toggle]');
  const nav    = document.querySelector('[data-mobile-nav]');
  const overlay = document.getElementById('site-overlay');
  if (!toggle || !nav) return;

  const open = () => {
    toggle.setAttribute('aria-expanded', 'true');
    nav.removeAttribute('aria-hidden');
    nav.classList.add('is-open');
    overlay && overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  };

  const close = () => {
    toggle.setAttribute('aria-expanded', 'false');
    nav.setAttribute('aria-hidden', 'true');
    nav.classList.remove('is-open');
    overlay && overlay.classList.remove('is-active');
    document.body.style.overflow = '';
  };

  toggle.addEventListener('click', () => {
    toggle.getAttribute('aria-expanded') === 'true' ? close() : open();
  });

  overlay && overlay.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
})();

/* ── Scroll-triggered reveal ───────────────────────────────── */
(function initReveal() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('.reveal').forEach(el => {
      el.classList.add('is-visible');
    });
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
})();

/* ── Rotating footer mantra ────────────────────────────────── */
(function initRotatingMantra() {
  const el = document.querySelector('[data-rotating-mantra] #footer-mantra');
  if (!el) return;

  const mantras = [
    '"You are the dreamer awakening within the dream."',
    '"Let there be light."',
    '"I AM."',
    '"Remember who you truly are."',
    '"The light you seek lives within you."',
  ];

  let i = 0;
  setInterval(() => {
    el.style.opacity = '0';
    setTimeout(() => {
      i = (i + 1) % mantras.length;
      el.textContent = mantras[i];
      el.style.opacity = '1';
    }, 700);
  }, 5000);
})();

/* ── Product gallery ───────────────────────────────────────── */
(function initGallery() {
  const thumbs = Array.from(document.querySelectorAll('[data-gallery-thumb]'));
  const mainImg = document.getElementById('gallery-main-img');
  if (!thumbs.length || !mainImg) return;

  function activateThumb(thumb) {
    thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    const src = thumb.dataset.gallerySrc;
    if (!src) return;

    mainImg.style.transition = 'opacity 180ms ease';
    mainImg.style.opacity = '0';

    const preload = new Image();
    preload.onload = () => {
      mainImg.src = src;
      mainImg.srcset = '';
      mainImg.removeAttribute('sizes');
      mainImg.style.opacity = '1';
      setTimeout(() => { mainImg.style.transition = ''; }, 200);
    };
    preload.src = src;
  }

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => activateThumb(thumb));
  });

  function currentIndex() {
    return thumbs.findIndex(t => t.classList.contains('is-active'));
  }

  const prevBtn = document.querySelector('[data-gallery-prev]');
  const nextBtn = document.querySelector('[data-gallery-next]');

  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = currentIndex();
      activateThumb(thumbs[(idx - 1 + thumbs.length) % thumbs.length]);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = currentIndex();
      activateThumb(thumbs[(idx + 1) % thumbs.length]);
    });
  }
})();

/* ── Quantity picker ───────────────────────────────────────── */
(function initQtyPicker() {
  document.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach(btn => {
    btn.addEventListener('click', () => {
      const isPlus = btn.hasAttribute('data-qty-plus');
      const input  = btn.closest('.qty-picker__controls').querySelector('.qty-picker__input');
      if (!input) return;
      const current = parseInt(input.value, 10) || 1;
      const next    = isPlus ? current + 1 : Math.max(1, current - 1);
      input.value   = next;
    });
  });
})();

/* ── Variant picker ────────────────────────────────────────── */
(function initVariantPicker() {
  const form = document.querySelector('[id^="product-form-"]');
  if (!form) return;

  const variantsEl = document.getElementById('product-variants-json');
  if (!variantsEl) return;

  const variants     = JSON.parse(variantsEl.textContent);
  const idInput      = form.querySelector('input[name="id"]');
  const submitBtn    = form.querySelector('[type="submit"][name="add"]');
  const optionGroups = Array.from(form.querySelectorAll('.variant-picker'));

  // ── Money formatter ──────────────────────────────────────────
  const moneyFormat = document.querySelector('meta[name="money-format"]')?.content || '${{amount}}';
  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return moneyFormat
      .replace('{{amount}}', dollars)
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
      .replace('{{amount_with_comma_separator}}', dollars.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.floor(cents / 100)));
  }


  // ── Find selected variant from all checked radios ────────────
  function getSelectedVariant() {
    const chosen = optionGroups.map(fg => {
      const checked = fg.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
    return variants.find(v =>
      chosen.every((val, i) => val === null || v[`option${i + 1}`] === val)
    );
  }

  // ── Update UI when selection changes ─────────────────────────
  function updateUI() {
    // Active pill highlight
    form.querySelectorAll('.variant-picker input[type="radio"]').forEach(radio => {
      radio.nextElementSibling?.classList.toggle('is-selected', radio.checked);
    });

    const variant = getSelectedVariant();
    if (!variant) return;

    if (idInput) idInput.value = variant.id;

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      submitBtn.toggleAttribute('aria-disabled', !variant.available);
      submitBtn.textContent = variant.available
        ? (submitBtn.dataset.addToCart || 'Add to Cart')
        : (submitBtn.dataset.soldOut   || 'Sold Out');
    }

    // ── Switch gallery to variant image ─────────────────────────
    if (variant.featured_image) {
      const varThumb = document.querySelector(
        `[data-gallery-thumb="${variant.featured_image.id}"]`
      );
      if (varThumb) varThumb.click();
    }

    // ── Update main price display ────────────────────────────────
    const priceBox     = document.querySelector('.main-product__price');
    const currentEl    = priceBox?.querySelector('.price__current');
    const compareWrap  = priceBox?.querySelector('.price__compare');
    const soldOutEl    = priceBox?.querySelector('.price__sold-out');

    if (currentEl) {
      if (!variant.available) {
        currentEl.style.display    = 'none';
        if (compareWrap) compareWrap.style.display = 'none';
        if (soldOutEl)   soldOutEl.style.display   = '';
      } else {
        if (soldOutEl) soldOutEl.style.display = 'none';
        currentEl.style.display = '';
        currentEl.textContent   = formatMoney(variant.price);

        if (compareWrap) {
          const compareS = compareWrap.querySelector('s');
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            if (compareS) compareS.textContent = formatMoney(variant.compare_at_price);
            compareWrap.style.display = '';
            currentEl.classList.add('text-gold');
          } else {
            compareWrap.style.display = 'none';
            currentEl.classList.remove('text-gold');
          }
        }
      }
    }
  }

  form.querySelectorAll('.variant-picker input[type="radio"]').forEach(radio => {
    radio.addEventListener('change', updateUI);
  });

  updateUI();
})();

/* ── 404 pulsing sun ───────────────────────────────────────── */
(function init404() {
  if (!document.body.classList.contains('template-404')) return;
  const icon = document.querySelector('.icon-sun');
  if (icon) icon.style.animation = 'pulse-gold 3s ease-in-out infinite';
})();
